import { NetworkStatus, useReactiveVar } from "@apollo/client";
import React, { useCallback, useMemo } from "react";

import ShareActionItem from "@/components/organisms/ShareActionItem";
import ShareView from "./ShareView";

import { ListRenderItem } from "@/components/atoms/FlashList";
import { Spinner } from "@/components/atoms/Spinner";
import { useCurrentUser } from "@/features/auth";
import {
  useWatchlistListMyOrganizationsOrganizationUsersQuery,
  WatchlistUserFragment,
  WatchlistUserWatchlistFragment,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";
import { changeShareVar } from "@/stores/common";

/**
 *
 */
interface Props {
  watchlist: WatchlistWatchlistMetaFragment;
  isMyWatchList: boolean;
  collaborators: WatchlistUserFragment[];
  userWatchLists: WatchlistUserWatchlistFragment[];
}

/**
 * Container component for watchlist sharing modal with user management
 * @param root0 - Component props
 * @param root0.watchlist - Watchlist object to share with collaborators
 * @param root0.isMyWatchList - Whether current user owns the watchlist
 * @param root0.collaborators - Array of users with access to the watchlist
 * @param root0.userWatchLists - Array of watchlist assignments per user
 */
const ShareContainer = ({
  watchlist,
  isMyWatchList,
  collaborators,
  userWatchLists,
}: Props) => {
  const isShared = useReactiveVar(changeShareVar);
  const {
    data: userData,
    loading,
    networkStatus,
    refetch,
    fetchMore,
  } = useWatchlistListMyOrganizationsOrganizationUsersQuery({
    variables: { first: 25 },
  });

  const handleClose = useCallback(() => changeShareVar(false), []);
  const currentUser = useCurrentUser();
  const isRefreshing = networkStatus === NetworkStatus.refetch;

  const users = useMemo(
    () =>
      (userData?.listMyOrganizationsOrganizationUsers?.edges ?? [])
        .map(organizationUser => organizationUser?.node.user)
        .filter(
          (user): user is WatchlistUserFragment =>
            user !== null && user !== undefined
        ),
    [userData?.listMyOrganizationsOrganizationUsers?.edges]
  );

  const fileteredUsers = useMemo(
    () =>
      // If the owner is viewing the watchlist, show all users, otherwise show only collaborators
      users.filter(user =>
        isMyWatchList
          ? user.id !== currentUser?.id
          : collaborators.find(collaborator => collaborator?.id === user?.id)
      ),
    [collaborators, currentUser?.id, isMyWatchList, users]
  );

  const handleLoadMore = useCallback(() => {
    const { hasNextPage, endCursor } =
      userData?.listMyOrganizationsOrganizationUsers?.pageInfo ?? {};
    if (hasNextPage && !loading) {
      fetchMore({ variables: { after: endCursor } });
    }
  }, [
    userData?.listMyOrganizationsOrganizationUsers?.pageInfo,
    loading,
    fetchMore,
  ]);

  const handleRefresh = useCallback(() => refetch().catch(() => {}), [refetch]);

  const renderShareActionItem: ListRenderItem<WatchlistUserFragment> =
    useCallback(
      ({ item }) => (
        <ShareActionItem
          watchlistId={watchlist.id}
          userWatchlist={userWatchLists.find(
            userWatchlist => userWatchlist.user.id === item.id
          )}
          user={item}
          isMeInWatchlist={item.id === currentUser?.id}
          isMyWatchlist={isMyWatchList}
          onCloseShare={handleClose}
        />
      ),
      [
        currentUser?.id,
        handleClose,
        isMyWatchList,
        userWatchLists,
        watchlist.id,
      ]
    );

  const renderListFooterComponent = useCallback(
    () =>
      loading && !isRefreshing && users.length ? (
        <Spinner size="large" color="white" />
      ) : null,
    [loading, isRefreshing, users.length]
  );

  return (
    <ShareView
      isOpen={isShared}
      onClose={handleClose}
      users={fileteredUsers}
      watchlist={watchlist}
      refreshing={isRefreshing}
      onLoadMore={handleLoadMore}
      onRefresh={handleRefresh}
      renderShareActionItem={renderShareActionItem}
      renderListFooterComponent={renderListFooterComponent}
    />
  );
};

export default ShareContainer;
